import { AUTH_TOKEN } from "constants/AuthConstant";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSectionsState } from "store/slices/agesectionsSlice";
import { isAuth } from "./isAuth";
import { setAuthUser, signOutSuccess } from "store/slices/authSlice";
import { refreshAccessToken, isRefreshingToken } from "refreshToken";

const useSections = () => {
  const [sections, setSections] = useState(null);
  const [section, setSection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const baseUrl = `${BASE_URL}/ageSections`;
  const dispatch = useDispatch();
  const tokenFromState = useSelector((state) => state.auth.token);

  // Vérifie la cohérence du token entre le state et le localStorage
  if (localStorage.getItem(AUTH_TOKEN) !== tokenFromState) {
    localStorage.removeItem(AUTH_TOKEN);
    dispatch(signOutSuccess());
    dispatch(setAuthUser({}));
  }

  // Fonction générique pour les appels API avec gestion du token
  const authFetch = async (url, options = {}, navigate) => {
    let token = tokenFromState || localStorage.getItem(AUTH_TOKEN);
    
    // Si pas de token et pas de refresh en cours, on tente un refresh
    if (!token && !isRefreshingToken()) {
      token = await refreshAccessToken();
    }

    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    // Si token expiré, on tente un refresh
    if (response.status === 401) {
      token = await refreshAccessToken();
      if (!token) {
        localStorage.removeItem(AUTH_TOKEN);
        dispatch(signOutSuccess());
        dispatch(setAuthUser({}));
        if (navigate) navigate("/login");
        throw new Error("Session expired");
      }

      // On réessaye avec le nouveau token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return response;
  };

  const addSection = async (data, navigate) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(baseUrl, {
        method: "POST",
        body: JSON.stringify(data),
      }, navigate);

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return;
      }

      const responseData = await response.json();
      setSection(responseData);
      await getAllSections(navigate);
    } catch (err) {
      console.error("Failed to add section", err);
      setError(err.message || "Failed to add section");
    } finally {
      setIsLoading(false);
    }
  };

  const getAllSections = async (navigate) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(baseUrl, { method: "GET" }, navigate);

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return;
      }

      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : [];

      if (Array.isArray(data)) {
        setSections(data);
        dispatch(setSectionsState(data));
      } else {
        throw new Error("Invalid sections data format");
      }
    } catch (err) {
      console.error("Failed to get sections:", err);
      setError(err.message || "Failed to get sections");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSection = async (id, navigate) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(`${baseUrl}/${id}`, {
        method: "DELETE",
      }, navigate);

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return;
      }

      setSection([]);
      await getAllSections(navigate);
    } catch (err) {
      console.error("Failed to delete section", err);
      setError(err.message || "Failed to delete section");
    } finally {
      setIsLoading(false);
    }
  };

  const updateSection = async (id, data, navigate) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(`${baseUrl}/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }, navigate);

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return;
      }

      const responseData = await response.json();
      await getAllSections(navigate);
      return responseData;
    } catch (err) {
      console.error("Failed to update section", err);
      setError(err.message || "Failed to update section");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getSectionById = async (id, navigate) => {
    if (!id) return null;
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(`${baseUrl}/${id}`, {
        method: "GET",
      }, navigate);

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return null;
      }

      return await response.json();
    } catch (err) {
      console.error("Failed to get section", err);
      setError(err.message || "Failed to get section");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Gestion centralisée des erreurs API
  const handleApiError = (errorData) => {
    if (!isAuth(errorData)) {
      localStorage.removeItem(AUTH_TOKEN);
      dispatch(signOutSuccess());
      dispatch(setAuthUser({}));
    }

    if (errorData?.statusCode === 500) {
      setError("Internal server error");
      return;
    }

    if (Array.isArray(errorData?.data?.message)) {
      setError(errorData.data.message[0]);
      return;
    }

    if (errorData?.data?.message) {
      setError(errorData.data.message);
      return;
    }

    if (errorData?.message) {
      setError(errorData.message);
      return;
    }

    setError("Unknown error occurred");
  };

  return {
    getSectionById,
    deleteSection,
    updateSection,
    addSection,
    section,
    sections,
    getAllSections,
    setSections,
    setSection,
    isLoading,
    error,
    setError,
  };
};

export default useSections;